// src/App.js
import React, { useState, useEffect, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import BottomNav from './components/BottomNav';
import HomePage from './pages/HomePage';
import Dashboard from './pages/Dashboard';   // legacy home screen, kept at /dashboard-legacy
import ScanPage from './pages/ScanPage';
import ItemDetail from './pages/ItemDetail';
import MyItems from './pages/MyItems';
import SearchPage from './pages/SearchPage';
import AdminPage from './pages/AdminPage';
import LoginPage from './pages/LoginPage';
import NoAccessPage from './pages/NoAccessPage';
import TransferPage from './pages/TransferPage';
import AcceptTransferPage from './pages/AcceptTransferPage';
import DeliverPage from './pages/DeliverPage';
import ScanMultiplePage from './pages/ScanMultiplePage';   // also replaces the old Load Truck screen
import IncomingPage from './pages/IncomingPage';
import AddItemPage from './pages/AddItemPage';
import EditItemPage from './pages/EditItemPage';
import DirectoryChangesModal from './components/DirectoryChangesModal';
import { isFullAccess } from './permissions';
import { getDirectoryChanges, checkSharedSignIn, sharedSignOut } from './services/api';

// Simple auth context
export const AuthContext = React.createContext(null);

// Shared sign-in only applies when this app itself is open at an nxtgc.com
// address. Opened at the old railway.app address, the browser will not share
// the nxtgc.com sign-in cookie with the server, so the server's "nobody is
// signed in" answer would wrongly sign people out on every load.
const SHARED_SIGN_IN_PAGE = /(^|\.)nxtgc\.com$/i.test(window.location.hostname);

export default function App() {
  const [worker, setWorker] = useState(() => {
    const saved = localStorage.getItem('ngc_worker');
    return saved ? JSON.parse(saved) : null;
  });

  const login = (workerData, token) => {
    localStorage.setItem('ngc_worker', JSON.stringify(workerData));
    localStorage.setItem('ngc_token', token);
    setWorker(workerData);
  };

  const logout = () => {
    if (SHARED_SIGN_IN_PAGE) sharedSignOut().catch(() => {}); // signs out of the other NGC apps too
    localStorage.removeItem('ngc_worker');
    localStorage.removeItem('ngc_token');
    setWorker(null);
  };

  // Shared sign-in with the Labor Board and Truck Schedule (nxtgc.com
  // addresses only; elsewhere the server answers { enabled: false }):
  // signed in there -> signed in here; signed out there -> signed out here.
  const [ssoChecked, setSsoChecked] = useState(!SHARED_SIGN_IN_PAGE);
  useEffect(() => {
    if (!SHARED_SIGN_IN_PAGE) return undefined;
    let cancelled = false;
    checkSharedSignIn()
      .then(({ data }) => {
        if (cancelled || !data.enabled) return;
        const saved = JSON.parse(localStorage.getItem('ngc_worker') || 'null');
        if (data.worker && data.token) {
          if (!saved || saved.worker_id !== data.worker.worker_id) {
            localStorage.setItem('ngc_worker', JSON.stringify(data.worker));
            localStorage.setItem('ngc_token', data.token);
            setWorker(data.worker);
          }
        } else if (saved) {
          localStorage.removeItem('ngc_worker');
          localStorage.removeItem('ngc_token');
          setWorker(null);
        }
      })
      .catch(() => { /* shared sign-in unavailable: normal sign-in still works */ })
      .finally(() => { if (!cancelled) setSsoChecked(true); });
    return () => { cancelled = true; };
  }, []);

  // Admin pages + add / edit items: Full access (permissions table, 10/23)
  const isAdmin = isFullAccess(worker?.role);

  // Until the shared sign-in check answers, don't flash the sign-in page.
  if (!worker && !ssoChecked) return <div className="loading" style={{ minHeight: '100vh' }}><div className="spinner" /></div>;

  return (
    <AuthContext.Provider value={{ worker, login, logout, isAdmin }}>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login" element={
            worker ? <Navigate to="/" replace /> : <LoginPage />
          } />
          {/* SMS acceptance link — no login required */}
          <Route path="/accept/:token" element={<AcceptTransferPage />} />

          {/* Protected */}
          <Route path="/*" element={
            !worker ? <Navigate to="/login" replace />
              : worker.role === 'no_access' ? <NoAccessPage />
              : <MainApp isAdmin={isAdmin} />
          } />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

function MainApp({ isAdmin }) {
  const { worker } = useContext(AuthContext);
  // Power Users see what the Procore sync changed in the directory on sign-in:
  // who was removed (OK to dismiss) and who was added or returned (assign a
  // role). Any failure just means no popup — it never blocks the app.
  const isPowerUser = ['power_user', 'general_superintendent'].includes(worker?.role);
  const [dirChanges, setDirChanges] = useState(null);

  useEffect(() => {
    if (!isPowerUser) return;
    let cancelled = false;
    getDirectoryChanges()
      .then(r => {
        const d = r.data || {};
        if (!cancelled && ((d.removed || []).length > 0 || (d.pending || []).length > 0)) setDirChanges(d);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isPowerUser]);

  return (
    <div className="screen">
      {dirChanges && (
        <DirectoryChangesModal
          removed={dirChanges.removed || []}
          pending={dirChanges.pending || []}
          onClose={() => setDirChanges(null)}
        />
      )}
      <Routes>
        <Route path="/"              element={<HomePage />} />
        {/* Old home screen — still works, deliberately not linked anywhere */}
        <Route path="/dashboard-legacy" element={<Dashboard />} />
        <Route path="/scan"          element={<ScanPage />} />
        <Route path="/item/:id"      element={<ItemDetail />} />
        <Route path="/transfer/:id"  element={<TransferPage />} />
        <Route path="/deliver"       element={<DeliverPage />} />
        <Route path="/load-truck"    element={<Navigate to="/scan-multiple?mode=truck" replace />} />
        <Route path="/scan-multiple" element={<ScanMultiplePage />} />
        <Route path="/incoming"      element={<IncomingPage />} />
        <Route path="/my-items"      element={<MyItems />} />
        <Route path="/search"        element={<SearchPage />} />
        <Route path="/admin"         element={isAdmin ? <AdminPage /> : <Navigate to="/" />} />
        <Route path="/admin/add"     element={isAdmin ? <AddItemPage /> : <Navigate to="/" />} />
        <Route path="/admin/edit/:id" element={isAdmin ? <EditItemPage /> : <Navigate to="/" />} />
        <Route path="*"              element={<Navigate to="/" replace />} />
      </Routes>
      <BottomNav />
    </div>
  );
}